/**
 * The Metals page tab *Purchase*: metal bought from an existing client (`POST /api/metals/purchases`).
 *
 * The operator fills one line at a time - metal, purity, grams, price per gram and the total. The
 * total follows grams × price, rounded to whole lei; typing the total instead sets the price to
 * the total over the grams. *Add line* keeps the line and opens an empty one; *Buy* sends the kept
 * lines and the open one when it is filled in.
 *
 * The client is paid in cash from a register with an open session, or - for an administrator
 * only - off the bank or out of the safe, which need no session; those two are entered in the cash
 * register (RC) only when the box is ticked.
 */
import { METAL_CATEGORIES, type MetalCategory } from './metalCategories';

export const PURCHASE_METALS = METAL_CATEGORIES;

/** The purities offered per metal; any other purity can be typed. */
export const PURCHASE_PURITY_OPTIONS: Readonly<Record<MetalCategory, readonly number[]>> = {
    AUR: [375, 585, 750, 800, 900, 916, 958, 999, 999.9],
    ARGINT: [800, 835, 900, 925, 999],
    PLATINA: [850, 900, 950, 999],
    PALADIU: [500, 950, 999],
};

/** `inventory_entry.fineness` is `decimal(5,2)`. */
export const MAX_PURCHASE_PURITY = 999.99;

export const PURCHASE_PAYMENT_METHODS = ['cash', 'bankTransfer', 'safe'] as const;

export type PurchasePaymentMethod = (typeof PURCHASE_PAYMENT_METHODS)[number];

/** The ways a user may pay: the bank and the safe are an administrator's. */
export const purchasePaymentMethods = (isAdmin: boolean): readonly PurchasePaymentMethod[] =>
    isAdmin ? PURCHASE_PAYMENT_METHODS : ['cash'];

/** The line being typed: any figure may still be missing. */
export interface PurchaseLineDraft {
    metalType: MetalCategory;
    purity: number | null;
    quantity: number | null;
    unitPrice: number | null;
    total: number | null;
}

/** A line ready to buy. */
export interface PurchaseLine {
    metalType: MetalCategory;
    purity: number;
    quantity: number;
    unitPrice: number;
    total: number;
}

export type PurchaseLineField = keyof PurchaseLineDraft;

/** A register a cash purchase can be paid from (`GET /api/metals/purchases/cash-points`). */
export interface PurchaseCashPoint {
    id: number;
    name: string;
    hasOpenSession: boolean;
}

/** The client found by the search box (`GET /api/clients?search=`). */
export interface PurchaseClient {
    ClNr: number;
    Nume: string | null;
    CNP?: string | null;
}

export interface PurchaseFormState {
    clientId: number | null;
    purchaseDate: string | null;
    lines: PurchaseLine[];
    draft: PurchaseLineDraft;
    registerPayment: boolean;
    paymentMethod: PurchasePaymentMethod;
    cashPointId: number | null;
    /** Enter a bank or safe payment in the cash register (RC); cash always is. */
    rc: boolean;
    addToStock: boolean;
}

/** The body of `POST /api/metals/purchases`. */
export interface PurchaseRequest {
    clientId: number;
    purchaseDate: string;
    lines: PurchaseLine[];
    registerPayment: boolean;
    payment: { method: PurchasePaymentMethod; cashPointId: number | null; rc: boolean } | null;
    addToStock: boolean;
}

/** `POST /api/metals/purchases` answers the purchase it recorded. */
export interface PurchaseResult {
    id: number;
    clientName: string;
    purchaseDate: string;
    totalRon: number;
    dispositionId: number | null;
    addedToStock: boolean;
}

const ERROR_PREFIX = 'metals.purchase.errors.';

export const roundTo = (value: number, decimals: number): number => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

/** A number typed into a free text box (the purity), or `null` for nothing or not a number. */
export const parseDecimalInput = (text: string): number | null => {
    const trimmed = text.trim().replace(',', '.');
    if (trimmed === '') {
        return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

const isPositive = (value: number | null): value is number =>
    value !== null && Number.isFinite(value) && value > 0;

export const emptyLineDraft = (metalType: MetalCategory = 'AUR'): PurchaseLineDraft => ({
    metalType,
    purity: null,
    quantity: null,
    unitPrice: null,
    total: null,
});

/** Grams × price per gram, in whole lei; `null` until both are known. */
export const lineTotal = (quantity: number | null, unitPrice: number | null): number | null =>
    isPositive(quantity) && unitPrice !== null ? roundTo(quantity * unitPrice, 0) : null;

/** The price per gram a typed total stands for; `null` without grams. */
export const unitPriceFromTotal = (quantity: number | null, total: number | null): number | null =>
    isPositive(quantity) && total !== null ? roundTo(total / quantity, 2) : null;

/**
 * The line after one field changed. The grams and the price recompute the total; the total
 * recomputes the price (`unitPriceFromTotal`), so the last figure typed is the one kept. A new
 * metal clears the purity, whose list depends on it.
 */
export function updateLineDraft(
    draft: PurchaseLineDraft,
    field: PurchaseLineField,
    value: MetalCategory | number | null,
): PurchaseLineDraft {
    if (field === 'metalType') {
        return { ...draft, metalType: value as MetalCategory, purity: null };
    }
    const number = value as number | null;
    if (field === 'purity') {
        return { ...draft, purity: number };
    }
    if (field === 'total') {
        return {
            ...draft,
            total: number,
            unitPrice: unitPriceFromTotal(draft.quantity, number) ?? draft.unitPrice,
        };
    }
    if (field === 'quantity' && draft.unitPrice === null) {
        // Grams typed after the total, before any price: the total stays and gives the price.
        return { ...draft, quantity: number, unitPrice: unitPriceFromTotal(number, draft.total) };
    }
    const next = { ...draft, [field]: number };
    return { ...next, total: lineTotal(next.quantity, next.unitPrice) ?? next.total };
}

export const isLineEmpty = (draft: PurchaseLineDraft): boolean =>
    draft.purity === null &&
    draft.quantity === null &&
    draft.unitPrice === null &&
    draft.total === null;

/** Every figure is there and positive, and the purity fits the stock's column. */
export const isLineComplete = (draft: PurchaseLineDraft): boolean =>
    isPositive(draft.purity) &&
    draft.purity <= MAX_PURCHASE_PURITY &&
    isPositive(draft.quantity) &&
    isPositive(draft.unitPrice) &&
    isPositive(draft.total);

/** A complete draft as a line; call only after `isLineComplete`. */
export const toPurchaseLine = (draft: PurchaseLineDraft): PurchaseLine => ({
    metalType: draft.metalType,
    purity: draft.purity ?? 0,
    quantity: draft.quantity ?? 0,
    unitPrice: draft.unitPrice ?? 0,
    total: draft.total ?? 0,
});

/** What the client is paid for the lines, in RON. */
export const purchaseTotal = (lines: readonly PurchaseLine[]): number =>
    roundTo(
        lines.reduce((sum, line) => sum + line.total, 0),
        2,
    );

/**
 * The lines a purchase is made of: the kept ones and the open one when filled in. An open line
 * that is only partly filled in stops the purchase, rather than being dropped unseen.
 */
export function collectPurchaseLines(
    lines: readonly PurchaseLine[],
    draft: PurchaseLineDraft,
): { lines: PurchaseLine[] } | { errorKey: string } {
    if (isLineEmpty(draft)) {
        return lines.length > 0 ? { lines: [...lines] } : { errorKey: `${ERROR_PREFIX}noLines` };
    }
    if (!isLineComplete(draft)) {
        return { errorKey: `${ERROR_PREFIX}incompleteLine` };
    }
    return { lines: [...lines, toPurchaseLine(draft)] };
}

/**
 * The request the form stands for, or the i18n key of what is still missing. `today` is the
 * shop's day (`YYYY-MM-DD`).
 */
export function buildPurchaseRequest(
    state: PurchaseFormState,
    today: string,
): { request: PurchaseRequest } | { errorKey: string } {
    if (state.clientId === null) {
        return { errorKey: `${ERROR_PREFIX}noClient` };
    }
    if (!state.purchaseDate) {
        return { errorKey: `${ERROR_PREFIX}noDate` };
    }
    if (state.purchaseDate > today) {
        return { errorKey: `${ERROR_PREFIX}futureDate` };
    }
    const collected = collectPurchaseLines(state.lines, state.draft);
    if ('errorKey' in collected) {
        return collected;
    }
    const isCash = state.paymentMethod === 'cash';
    if (state.registerPayment && isCash && state.cashPointId === null) {
        return { errorKey: `${ERROR_PREFIX}noCashPoint` };
    }
    const payment = state.registerPayment
        ? {
              method: state.paymentMethod,
              cashPointId: isCash ? state.cashPointId : null,
              rc: isCash || state.rc,
          }
        : null;
    return {
        request: {
            clientId: state.clientId,
            purchaseDate: state.purchaseDate,
            lines: collected.lines,
            registerPayment: state.registerPayment,
            payment,
            addToStock: state.addToStock,
        },
    };
}

/** How a found client is listed in the search box: name, then client number and CNP. */
export const purchaseClientLabel = (client: PurchaseClient): string => {
    const name = (client.Nume ?? '').trim();
    const cnp = client.CNP ? ` - ${client.CNP}` : '';
    return `${name} (#${client.ClNr})${cnp}`;
};

/** The register picked by default: this station's own, when it can pay now. */
export const defaultPurchaseCashPoint = (
    cashPoints: readonly PurchaseCashPoint[],
    workstationCashPointId: number | null,
): number | null =>
    cashPoints.find(
        cashPoint => cashPoint.id === workstationCashPointId && cashPoint.hasOpenSession,
    )?.id ?? null;
